import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api/client";
import Header from "../../components/layout/Header";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { maskCNPJInput, maskCEPInput, validateCNPJ, fetchViaCEP, fetchOpenCNPJ } from "../../utils/validate";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, Building2, MapPin, Contact, ShieldCheck, BookOpenCheck } from "lucide-react";

const empty = {
  razao_social: "", cnpj: "", logradouro: "", numero: "", complemento: "", bairro: "",
  cep: "", municipio: "", uf: "", telefone: "", email: "", contato_principal: "",
  atividade_principal: "", cnae: "", grau_risco: 1, num_funcionarios: 0,
  distancia_km_sede: 0, observacoes: "",
};

function FormSection({ icon: Icon, eyebrow, title, description, children }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm">
      <div className="flex items-start gap-3 border-b border-border/70 bg-muted/20 px-4 py-4 sm:px-6">
        <div className="grid size-10 shrink-0 place-items-center rounded-xl border border-primary/15 bg-primary/10 text-primary">
          <Icon className="size-4.5" />
        </div>
        <div>
          <div className="label-tech">{eyebrow}</div>
          <h2 className="mt-0.5 text-base font-semibold tracking-tight">{title}</h2>
          {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
        </div>
      </div>
      <div className="p-4 sm:p-6">{children}</div>
    </section>
  );
}

export default function ClientForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = id && id !== "novo";
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [nr4Loading, setNr4Loading] = useState(false);
  const [nr4Match, setNr4Match] = useState(null);
  const cnpjTimer = useRef(null);
  const lastCnpjLookup = useRef("");

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    api.get(`/clients/${id}`).then((r) => setForm(r.data)).catch(() => toast.error("Falha ao carregar")).finally(() => setLoading(false));
  }, [id, isEdit]);

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const lookupNR4 = async (cnae, { silent = false } = {}) => {
    const digits = String(cnae || "").replace(/\D/g, "");
    if (digits.length < 5) {
      setNr4Match(null);
      return null;
    }
    setNr4Loading(true);
    try {
      const r = await api.get(`/reference/nr4/cnae/${digits}`);
      setNr4Match(r.data);
      return r.data;
    } catch (err) {
      setNr4Match(null);
      if (!silent) toast.warning("CNAE não localizado no quadro da NR-4. Confira o Grau de Risco manualmente.");
      return null;
    } finally {
      setNr4Loading(false);
    }
  };

  const lookupCNPJ = async (digits) => {
    if (lastCnpjLookup.current === digits) return;
    lastCnpjLookup.current = digits;
    setCnpjLoading(true);
    try {
      const d = await fetchOpenCNPJ(digits);
      if (!d) { toast.error("CNPJ não encontrado na base pública."); return; }

      // CNPJ -> CNAE principal -> classe CNAE do Anexo I da NR-4 -> GR.
      // No match means no inference: keep the existing GR for technical review.
      const nr4 = d.cnae ? await lookupNR4(d.cnae, { silent: true }) : null;

      setForm((f) => {
        const fillIfEmpty = (cur, val) => (String(cur || "").trim() ? cur : (val || cur));
        return {
          ...f,
          razao_social: fillIfEmpty(f.razao_social, d.razao_social),
          // Principal activity and CNAE belong to the informed CNPJ, so they are
          // refreshed even when the form previously contained another value.
          atividade_principal: d.atividade_principal || f.atividade_principal,
          cnae: d.cnae || f.cnae,
          grau_risco: nr4?.grau_risco ?? f.grau_risco,
          logradouro: fillIfEmpty(f.logradouro, d.logradouro),
          numero: fillIfEmpty(f.numero, d.numero),
          complemento: fillIfEmpty(f.complemento, d.complemento),
          bairro: fillIfEmpty(f.bairro, d.bairro),
          cep: fillIfEmpty(f.cep, d.cep),
          municipio: fillIfEmpty(f.municipio, d.municipio),
          uf: fillIfEmpty(f.uf, d.uf),
          telefone: fillIfEmpty(f.telefone, d.telefone),
          email: fillIfEmpty(f.email, d.email),
        };
      });

      const situation = d.situacao && d.situacao !== "Ativa" ? ` · situação: ${d.situacao}` : "";
      if (nr4) {
        toast.success(`Dados carregados · ${nr4.cnae_classe} · GR ${nr4.grau_risco}${situation}`);
      } else {
        toast.success(`Dados cadastrais carregados${situation}`);
        if (d.cnae) toast.warning("CNAE principal carregado, mas sem correspondência automática na NR-4. Revise o GR.");
      }
    } finally {
      setCnpjLoading(false);
    }
  };

  const handleCNPJ = (raw) => {
    const masked = maskCNPJInput(raw);
    upd("cnpj")(masked);
    setNr4Match(null);
    const digits = masked.replace(/\D/g, "");
    if (cnpjTimer.current) clearTimeout(cnpjTimer.current);
    if (digits.length === 14 && validateCNPJ(masked)) {
      cnpjTimer.current = setTimeout(() => lookupCNPJ(digits), 600);
    }
  };

  const handleCNAEBlur = async () => {
    if (!form.cnae) return;
    const match = await lookupNR4(form.cnae);
    if (match) {
      setForm((f) => ({ ...f, grau_risco: match.grau_risco }));
      toast.success(`NR-4: ${match.cnae_classe} · Grau de Risco ${match.grau_risco}`);
    }
  };

  const handleCEP = async (cep) => {
    upd("cep")(maskCEPInput(cep));
    if (cep.replace(/\D/g, "").length === 8) {
      const data = await fetchViaCEP(cep);
      if (data) {
        setForm((f) => ({
          ...f, logradouro: data.logradouro || f.logradouro, bairro: data.bairro || f.bairro,
          municipio: data.localidade || f.municipio, uf: data.uf || f.uf,
        }));
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.razao_social) return toast.error("Informe a Razão Social");
    if (!validateCNPJ(form.cnpj)) return toast.error("CNPJ inválido");
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/clients/${id}`, form);
        toast.success("Cliente atualizado");
      } else {
        await api.post("/clients", form);
        toast.success("Cliente cadastrado");
      }
      navigate("/clientes");
    } catch (err) { toast.error(err.response?.data?.detail || "Falha ao salvar"); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Header
        title={isEdit ? "Editar cliente" : "Novo cliente"}
        subtitle={isEdit ? "Atualize os dados cadastrais, operacionais e de risco." : "Cadastre os dados que serão reutilizados em propostas e documentos técnicos."}
        actions={<Button variant="outline" onClick={() => navigate("/clientes")} data-testid="cancel-client-form-btn"><ArrowLeft className="size-4 mr-1" /> Voltar</Button>}
      />

      {loading ? (
        <div className="p-4 sm:p-6 lg:p-8">
          <div className="mx-auto max-w-6xl rounded-2xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">Carregando cadastro…</div>
        </div>
      ) : (
        <form onSubmit={submit} className="mx-auto w-full max-w-6xl space-y-5 p-4 sm:p-6 lg:p-8" data-testid="client-form">
          <FormSection
            icon={Building2}
            eyebrow="Identificação"
            title="Dados da empresa"
            description="Informações principais para propostas, documentos e identificação do cliente."
          >
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label className="label-tech">Razão Social *</Label>
                <Input value={form.razao_social} onChange={(e) => upd("razao_social")(e.target.value)} data-testid="client-razao-input" required />
              </div>
              <div>
                <Label className="label-tech flex items-center gap-2">
                  CNPJ *
                  {cnpjLoading && <span className="inline-flex items-center gap-1 text-xs text-primary font-normal"><Loader2 className="size-3 animate-spin" /> buscando dados…</span>}
                </Label>
                <Input value={form.cnpj} onChange={(e) => handleCNPJ(e.target.value)} placeholder="00.000.000/0000-00" data-testid="client-cnpj-input" required />
                <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">Ao informar um CNPJ válido, o CEO consulta os dados públicos e usa o CNAE principal para pesquisar automaticamente o Grau de Risco no Anexo I da NR-4.</p>
              </div>
            </div>
          </FormSection>

          <FormSection
            icon={MapPin}
            eyebrow="Localização"
            title="Endereço operacional"
            description="Usado para documentos técnicos, deslocamentos e composição comercial."
          >
            <div className="grid gap-4 md:grid-cols-4">
              <div>
                <Label className="label-tech">CEP</Label>
                <Input value={form.cep} onChange={(e) => handleCEP(e.target.value)} placeholder="00000-000" data-testid="client-cep-input" />
              </div>
              <div className="md:col-span-3">
                <Label className="label-tech">Logradouro</Label>
                <Input value={form.logradouro} onChange={(e) => upd("logradouro")(e.target.value)} data-testid="client-logradouro-input" />
              </div>
              <div>
                <Label className="label-tech">Número</Label>
                <Input value={form.numero} onChange={(e) => upd("numero")(e.target.value)} data-testid="client-numero-input" />
              </div>
              <div className="md:col-span-2">
                <Label className="label-tech">Complemento</Label>
                <Input value={form.complemento} onChange={(e) => upd("complemento")(e.target.value)} data-testid="client-complemento-input" />
              </div>
              <div>
                <Label className="label-tech">Bairro</Label>
                <Input value={form.bairro} onChange={(e) => upd("bairro")(e.target.value)} data-testid="client-bairro-input" />
              </div>
              <div className="md:col-span-3">
                <Label className="label-tech">Município</Label>
                <Input value={form.municipio} onChange={(e) => upd("municipio")(e.target.value)} data-testid="client-municipio-input" />
              </div>
              <div>
                <Label className="label-tech">UF</Label>
                <Input maxLength={2} value={form.uf} onChange={(e) => upd("uf")(e.target.value.toUpperCase())} data-testid="client-uf-input" />
              </div>
            </div>
          </FormSection>

          <FormSection
            icon={Contact}
            eyebrow="Relacionamento"
            title="Contato principal"
            description="Dados para comunicação comercial, agendamentos e envio de documentos."
          >
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label className="label-tech">Telefone</Label>
                <Input value={form.telefone} onChange={(e) => upd("telefone")(e.target.value)} data-testid="client-telefone-input" />
              </div>
              <div>
                <Label className="label-tech">E-mail</Label>
                <Input type="email" value={form.email} onChange={(e) => upd("email")(e.target.value)} data-testid="client-email-input" />
              </div>
              <div>
                <Label className="label-tech">Contato principal</Label>
                <Input value={form.contato_principal} onChange={(e) => upd("contato_principal")(e.target.value)} data-testid="client-contato-input" />
              </div>
            </div>
          </FormSection>

          <FormSection
            icon={ShieldCheck}
            eyebrow="SST e operação"
            title="Atividade, risco e dimensionamento"
            description="Base para enquadramento técnico, cálculos comerciais e geração de documentos."
          >
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label className="label-tech">Atividade Principal</Label>
                <Input value={form.atividade_principal} onChange={(e) => upd("atividade_principal")(e.target.value)} data-testid="client-atividade-input" />
              </div>
              <div>
                <Label className="label-tech flex items-center gap-2">
                  CNAE principal
                  {nr4Loading && <span className="inline-flex items-center gap-1 text-xs font-normal text-primary"><Loader2 className="size-3 animate-spin" /> consultando NR-4…</span>}
                </Label>
                <Input
                  value={form.cnae}
                  onChange={(e) => { upd("cnae")(e.target.value); setNr4Match(null); }}
                  onBlur={handleCNAEBlur}
                  placeholder="00.00-0-00"
                  data-testid="client-cnae-input"
                />
              </div>
              <div>
                <Label className="label-tech">Grau de Risco (NR-04)</Label>
                <Select value={String(form.grau_risco)} onValueChange={(v) => upd("grau_risco")(parseInt(v, 10))}>
                  <SelectTrigger data-testid="client-grau-risco-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1" data-testid="grau-1">Grau 1</SelectItem>
                    <SelectItem value="2" data-testid="grau-2">Grau 2</SelectItem>
                    <SelectItem value="3" data-testid="grau-3">Grau 3</SelectItem>
                    <SelectItem value="4" data-testid="grau-4">Grau 4</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-tech">Nº de funcionários</Label>
                <Input type="number" min="0" value={form.num_funcionarios} onChange={(e) => upd("num_funcionarios")(parseInt(e.target.value || 0, 10))} data-testid="client-num-func-input" />
              </div>

              {nr4Match && (
                <div className="md:col-span-2 flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-3" data-testid="client-nr4-match">
                  <div className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><BookOpenCheck className="size-4" /></div>
                  <div className="min-w-0 text-xs leading-relaxed">
                    <div className="font-semibold text-foreground">Enquadramento automático NR-4 · {nr4Match.cnae_classe} · GR {nr4Match.grau_risco}</div>
                    <div className="mt-0.5 text-muted-foreground">{nr4Match.descricao}</div>
                    <div className="mt-1 text-muted-foreground">Fonte: {nr4Match.fonte}. O GR permanece editável para revisão técnica, inclusive quando a atividade preponderante do estabelecimento exigir enquadramento superior.</div>
                  </div>
                </div>
              )}

              <div>
                <Label className="label-tech">Distância até Nova Friburgo (km)</Label>
                <Input type="number" min="0" step="0.1" value={form.distancia_km_sede || 0} onChange={(e) => upd("distancia_km_sede")(parseFloat(e.target.value || 0))} placeholder="0 se a cidade for Nova Friburgo" data-testid="client-distancia-km-input" />
              </div>
              <div className="md:col-span-2">
                <Label className="label-tech">Observações</Label>
                <Textarea value={form.observacoes} onChange={(e) => upd("observacoes")(e.target.value)} data-testid="client-obs-input" rows={4} />
              </div>
            </div>
          </FormSection>

          <div className="sticky bottom-3 z-20 rounded-2xl border border-border/80 bg-background/95 p-3 shadow-lg backdrop-blur sm:p-4">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="outline" onClick={() => navigate("/clientes")} data-testid="client-cancel-btn">Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="client-save-btn" size="lg">
                {saving ? <Loader2 className="size-4 mr-1 animate-spin" /> : <Save className="size-4 mr-1" />}
                {saving ? "Salvando..." : "Salvar cliente"}
              </Button>
            </div>
          </div>
        </form>
      )}
    </>
  );
}
